import React from 'react';
import DeleteWarning from '../deleteWarning';
import Paging from '../paging';
import Help from '../help';
import { systemText } from '../../helpers/string';
import { existsAndHasLength } from '../../helpers/array';

import './table.css';

const Table = props => {
	const [showDelete, setShowDelete] = React.useState(false);
	const [showBulkDelete, setShowBulkDelete] = React.useState(false);
	const [deleting, setDeleting] = React.useState(null);
	const [bulkValues, setBulkValues] = React.useState([]);
	const [bulkValuesShown, setBulkValuesShown] = React.useState([]);

	React.useEffect(() => {
		setBulkValuesShown(bulkValues.filter(b => props.data.some(d => d.id === b)));
	}, [bulkValues, props.data]);

	const deleteItem = confirm => {
		setShowDelete(false);
		if (confirm) {
			props.removeItem(deleting.id, deleting);
		}

		setDeleting(null);
	};

	const bulkDeleteItems = confirm => {
		setShowBulkDelete(false);
		if (confirm) {
			props.removeItem(
				bulkValues.filter(b => data.some(d => d.id === b && !d.cannotDelete)),
				props.data,
				true,
			);
		}
	};

	const sort = sortBy => {
		if (typeof props.sort === 'function') {
			props.sort(sortBy);
		}
	};

	const goToPage = page => {
		if (typeof props.goToPage === 'function') {
			props.goToPage(page);
		}
	};

	const { columns, data, options } = props;
	options.extraOptions = options.extraOptions || [];

	const getLengthOrNumber = o => {
		if (existsAndHasLength(o)) return o.length;
		return o;
	};

	const bulk = (checked, id) => {
		const temp = [...bulkValues.filter(b => b !== id)];
		if (checked) {
			setBulkValues(temp.concat([id]));
		} else {
			setBulkValues(temp);
		}
	};

	const bulkAll = () => {
		if (bulkValues.length !== data.length) {
			setBulkValues(data.reduce((a, c) => a.concat([c.id]), []));
		} else {
			setBulkValues([]);
		}
	};

	const bulkDeleteWarningMessage = () => {
		if (options.bulkDeleteWarning) return options.bulkDeleteWarning;

		const selected = bulkValuesShown.length;
		const selectedNotAllowed = data.filter(d => d.cannotDelete && bulkValuesShown.some(x => x === d.id)).length;

		const main =
			selected === 1
				? `${systemText('Admin/General/Table/Options/Delete/Warning', options.label)}${
						options.deleteExtra ? `\n${options.deleteExtra}` : ''
					}`
				: `${systemText('Admin/General/Table/Options/Bulk/Delete/Warning', bulkValuesShown.length, options.label)}`;

		const extra =
			selectedNotAllowed > 0
				? `\n\n${systemText(
						'Admin/General/Table/Options/Bulk/Delete/Warning/Extra',
						selectedNotAllowed,
						options.label,
					)}`
				: '';

		return `${main}${extra}`;
	};

	const showOptions = options.showActive || options.showDelete || options.extraOptions.length > 0;
	const showBulk =
		!options.hideBulk && (options.showActive || options.showDelete || options.extraOptions.some(x => x.bulk));

	if (data) options.recordCount = options.recordCount || data.length;

	const renderExtraBulkOption = (o, i) => {
		return (
			<button
				key={`bulk-${i}`}
				className={`btn ${typeof o.btnClass === 'function' ? o.btnClass({}) : o.btnClass}`}
				disabled={!bulkValuesShown?.length}
				onClick={() => o.onClick(bulkValuesShown)}
			>
				<i className={`fa ${o.icon} mr10`} />
				{o.bulk}
			</button>
		);
	};

	const bulkOptions = !showBulk ? null : (
		<>
			<div className="table-btns">
				{options.extraOptions.filter(x => x.bulk && !x.after).map((o, i) => renderExtraBulkOption(o, i))}
				{options.showActive && (
					<>
						<button
							className="btn btn-success btn-power"
							onClick={() => props.activateItems(bulkValuesShown, data, true)}
							disabled={!bulkValuesShown.some(b => data.some(d => d.id === b && !d.active))}
						>
							<i className="fa fa-power-off mr10" />
							{systemText('Admin/General/Table/Options/Bulk/Activate')}
						</button>
						<button
							className="btn btn-warning btn-power"
							onClick={() => props.activateItems(bulkValuesShown, data, false)}
							disabled={!bulkValuesShown.some(b => data.some(d => d.id === b && d.active))}
						>
							<i className="fa fa-power-off mr10" />
							{systemText('Admin/General/Table/Options/Bulk/DeActivate')}
						</button>
					</>
				)}
				{options.showDelete && (
					<button
						className="btn btn-danger"
						onClick={() => setShowBulkDelete(true)}
						disabled={!bulkValuesShown.some(b => data.some(d => !d.cannotDelete && d.id === b))}
					>
						<i className="fa fa-trash mr10" />
						{systemText('Admin/General/Table/Options/Bulk/Delete')}
					</button>
				)}
				{options.extraOptions.filter(x => x.bulk && x.after).map((o, i) => renderExtraBulkOption(o, i))}
			</div>
			{!options.hideBulkLabel && (
				<div className="mt5" style={{ color: '#a0a0a0' }}>
					{systemText('Admin/General/Table/Options/Bulk/Label')}
				</div>
			)}
		</>
	);

	const renderExtraOption = (o, d, i, labelName) => {
		// renderInstead lets a row show its own control in place of the option's button.
		const instead = o.renderInstead && o.renderInstead(d);
		if (instead) {
			return (
				<div className="btn-badge" key={`btn-${i}-instead`}>
					{instead}
				</div>
			);
		}

		const title = typeof o.label === 'function' ? o.label(d) : `${o.label} ${labelName}`;
		return (
			<div className="btn-badge" key={`btn-${i}-${typeof o.icon === 'function' ? o.icon(d) : o.icon}`}>
				<button
					disabled={o.disabled && o.disabled(d)}
					className={`mr10 btn btn-sm ${typeof o.btnClass === 'function' ? o.btnClass(d) : o.btnClass}`}
					title={title}
					onClick={() => o.onClick(d)}
				>
					{o.icon ? <i className={`fa ${typeof o.icon === 'function' ? o.icon(d) : o.icon}`} /> : <>{title}</>}
				</button>
				{o.count && <span className="badge label label-warning">{getLengthOrNumber(d[o.count])}</span>}
			</div>
		);
	};

	return (
		<div>
			{(!data || data.length === 0) && (
				<span className="no-results">{systemText('Admin/General/Table/NoResults')}</span>
			)}
			{data && data.length > 0 && (
				<div>
					{options.bulkOptionsBefore && <div className="mb10">{bulkOptions}</div>}
					<div className="table-scroller">
						<table className={`table ${options.tableClass ? options.tableClass : 'table-striped'}`}>
							<thead>
								<tr>
									{showBulk && (
										<th width="1" className="bulk">
											<button
												onClick={() => bulkAll()}
												className="btn-link"
												data-testid="table-header-select-all"
											>
												<i
													style={{ width: '10px' }}
													className={`pointer fa ${
														bulkValuesShown.length === data.length
															? 'fa-check-square text-success'
															: 'fa-square'
													}`}
												/>
											</button>
										</th>
									)}
									{columns.map((c, i) => {
										return (
											<th
												key={`col-${i}`}
												data-testid={`table-column-${i}`}
												className={`${c.className} ${
													c.isNumeric
														? `text-right ${options.thClass} ${
																c.label.indexOf('e4') === 0 ? 'non-capitalise' : ''
															}`
														: options.thClass
												}`}
											>
												<div className="flex flex-row flex-center">
													<button className="btn-link" onClick={() => sort(c.dataProperty)}>
														{c.label}
													</button>
													{c.help && <Help id={`help_${c.dataProperty}`}>{c.help}</Help>}
													{options.sortBy === c.dataProperty && (
														<i className={`fa fa-caret-${options.ascending ? 'up' : 'down'}`} />
													)}
												</div>
											</th>
										);
									})}

									{showOptions && (
										<th className={`text-right ${options.thClass}`}>
											{!options.hideOptionsHeader && systemText('Admin/General/Table/Options/Label')}
										</th>
									)}
								</tr>
							</thead>
							<tbody>
								{data &&
									data.map((d, i) => {
										const format = (value, col) => {
											if (col.toggle)
												value = value
													? systemText('Admin/General/Table/Data/Yes')
													: systemText('Admin/General/Table/Data/No');
											if (col.showCount) value = value ? value.length : 0;
											if (col.percentage) value = value || value === 0 ? `${value.toFixed()}%` : '-';
											if (col.format) value = col.format(value, d);
											return col.isNumeric || col.showCount || value ? value : '-';
										};

										const labelName =
											d[options.labelOverride ? options.labelOverride : columns[0].dataProperty];

										return (
											<tr
												key={`row-${i}-${d['id']}`}
												className={
													typeof options.trClass === 'function'
														? options.trClass(d)
														: options.trClass
												}
											>
												{showBulk && (
													<td width="1">
														<button
															data-testid="table-row-select"
															onClick={e => bulk(!bulkValues.some(b => b === d.id), d.id)}
															className="btn-link"
														>
															<i
																style={{ width: '10px' }}
																className={`pointer fa ${
																	bulkValues.some(b => b === d.id)
																		? 'fa-check-square text-success'
																		: 'fa-square'
																}`}
															/>
														</button>
													</td>
												)}
												{columns.map((c, x) => (
													<td
														key={`cell-${i}-${x}`}
														data-testid={`table-column-${x}-data`}
														className={c.className}
													>
														<div>
															{c.renderer ? (
																x === (options.mainColumnIndex || 0) && props.setCurrent ? (
																	<button
																		className="btn-link"
																		onClick={() => props.setCurrent(d, c)}
																	>
																		{c.renderer(d)}
																	</button>
																) : (
																	c.renderer(d)
																)
															) : x === (options.mainColumnIndex || 0) && props.setCurrent ? (
																<button
																	className="btn-link"
																	onClick={() => props.setCurrent(d, c)}
																>
																	{format(d[c.dataProperty], c)}
																</button>
															) : (!props.setCurrent || x > 0) &&
															  c.onClick &&
															  (d[c.dataProperty] || c.showCount || options.clickOnZero) ? (
																<button
																	className="btn-link"
																	onClick={() => c.onClick(d, c)}
																	style={{ display: 'block' }}
																	title={
																		options.clickText ||
																		systemText('Admin/General/Table/More')
																	}
																>
																	{format(d[c.dataProperty], c)}
																</button>
															) : (
																<span>{format(d[c.dataProperty], c)}</span>
															)}
														</div>
													</td>
												))}
												{showOptions && (
													<td key={`cell-${i}-options`} className="text-right">
														<div className="table-btns no-wrap text-right inline-flex">
															{options.extraOptions
																?.filter(o => !o.after && !o.hideStandard)
																.map(o => renderExtraOption(o, d, i, labelName))}
															{options.showActive && (
																<button
																	className={`btn btn-sm btn-power btn-${
																		d.active ? 'success' : 'warning'
																	}`}
																	title={systemText(
																		`Admin/General/Table/Options/${
																			!d.active ? 'DeActivate' : 'Active'
																		}`,
																		labelName,
																	)}
																	disabled={d.activeDisabled}
																	onClick={() => {
																		props.activateItems([d.id], data, !d.active);
																	}}
																>
																	<i className="fa fa-power-off" />
																</button>
															)}
															{options.showDelete && (
																<button
																	className="btn btn-sm btn-danger"
																	title={
																		!d.cannotDelete
																			? systemText(
																					'Admin/General/Table/Options/Delete',
																					labelName,
																				)
																			: options.cannotDeleteMessage ||
																				options.cannotDeleteMessageDefault
																	}
																	onClick={() => {
																		setShowDelete(true);
																		setDeleting(d);
																	}}
																	disabled={d.cannotDelete}
																>
																	<i className="fa fa-trash" />
																</button>
															)}
															{options.extraOptions
																?.filter(o => o.after && !o.hideStandard)
																.map(o => renderExtraOption(o, d, i, labelName))}
														</div>
													</td>
												)}
											</tr>
										);
									})}
							</tbody>
						</table>
					</div>
					{!options.bulkOptionsBefore && bulkOptions}
					{!options.hidePaging && (
						<div className="text-center">
							<Paging {...options} goToPage={goToPage} />
						</div>
					)}
				</div>
			)}
			<DeleteWarning
				message={bulkDeleteWarningMessage()}
				show={showBulkDelete}
				close={confirm => bulkDeleteItems(confirm)}
			/>
			<DeleteWarning
				message={`${systemText('Admin/General/Table/Options/Delete/Warning', options.label)}${
					options.deleteExtra ? `\n${options.deleteExtra}` : ''
				}`}
				show={showDelete}
				close={confirm => deleteItem(confirm)}
			/>
		</div>
	);
};

export default Table;
